import { FC, memo, useMemo } from 'react';
import { Link } from 'react-router-dom';

import { dom, json, uuid } from 'app/utils';
import { Button as Component, ButtonProps as Props, Ripple } from '../external';
import { Icon } from './icon';
import { Tooltip } from './tooltip';
import { IComponent, ITooltipOptions } from '../types';

export interface IButtonBase extends IComponent {
  icon?: string;
  label?: string;
  link?: boolean;
  outlined?: boolean;
  rounded?: boolean;
  severity?: 'primary' | 'secondary' | 'success' | 'info' | 'warning' | 'danger' | 'help';
  size?: 'sm' | 'md' | 'lg';
  text?: boolean;

  tooltip?: string | ITooltipOptions;
  badge?: string;
  loading?: boolean;
}

export interface IButton extends IButtonBase {
  type?: 'button' | 'submit' | 'reset';
  href?: string;

  onClick?: (e: any) => void;
}

const sizes: Record<string, Props['size']> = {
  'sm': 'small',
  'lg': 'large'
};

export const Button: FC<IButton> = memo(({ id, className, style, disabled, icon, label, link, outlined, rounded, severity, size, text, type, href, tooltip, badge, loading, onClick }) => {
  const sizing = sizes[size ?? 'md'];
  if (severity === 'primary') severity = undefined;
  if (typeof tooltip === 'string') tooltip = { label: tooltip, position: 'bottom' };

  const identifier = useMemo(() => uuid.v4(), []);
  if (href && !disabled) {
    const anchor: any = { id, style, disabled,
      className: dom.className('i-component', 'i-button', 'p-button', 'p-component', { [`p-button-${severity}`]: severity, [`p-button-${sizing}`]: sizing, 'p-button-link': link, 'p-button-outlined': outlined, 'p-button-text': text, 'p-button-rounded': rounded, 'p-button-icon-only': (icon && !label) }, className),
      to: href,

      'data-pc-id': identifier,
    };

    return (
      <>
        <Link { ...anchor }>
          {icon && <Icon name={icon} size={size} className={[ 'p-button-icon p-c p-button-icon-left', { 'mr-3': label } ]} />}
          {label && <span className="p-button-label p-c" data-pc-section="label">{ label }</span>}
          <Ripple />
        </Link>
        {tooltip && <Tooltip { ...tooltip } target={`.i-button[data-pc-id="${identifier}"]`} />}
      </>
    );
  }

  const button: Props = { id, style, disabled, label, link, text, outlined, rounded, severity, badge, loading, onClick,
    className: dom.className('i-component', 'i-button', className),
    icon: (icon ? <Icon name={icon} size={size} className={{ 'mr-3': label }} /> : undefined),
    size: sizing,
    type: (type ?? 'button'),

    tooltip: tooltip?.label,
    tooltipOptions: json.process(tooltip, { '$.label': false,
      '$.className': (cls, path): [string, any] => [path, dom.className('i-component', 'i-tooltip', cls)],
    }),
  };

  return (
    <Component { ...button } />
  );
});
